/**
 * VictoryOverlay Component
 *
 * Full-screen overlay that displays the combat result when battle ends.
 * Shows victory/defeat/draw outcome, statistics, and action buttons.
 *
 * Used in:
 * - Battle page (when combat completes)
 */

import { useMemo, useEffect, useState } from 'react';
import styles from './VictoryOverlay.module.css';
import { OutcomeDisplay } from './OutcomeDisplay';
import StatisticsDisplay from './StatisticsDisplay';
import ActionButtons from './ActionButtons';
import { CreatureRosterDisplay } from './CreatureRosterDisplay';
import { BattleLogDisplay } from './BattleLogDisplay';
import { calculateCreaturePerformance, calculateMVP } from '../../utils/combat-stats';
import { getCombatLogService, type CombatLogEntry } from '../../services/combat-log-service';
import type { CombatResult, PlayerId, Outcome } from './types';

interface SeriesRecord {
  player1Wins: number;
  player2Wins: number;
  draws: number;
}

export interface VictoryOverlayProps {
  /** Combat result data */
  result: CombatResult;
  /** Current player's ID */
  currentPlayerId: PlayerId;
  /** Callback when leaving match to return to lobby */
  onReturnToLobby: () => void;
  /** Callback when Fight Again is clicked (rematch) */
  onFightAgain?: () => void;
  /** Winner's display name (for victory banner) */
  winnerDisplayName?: string;
  /** Series record between players */
  seriesRecord?: SeriesRecord;
}

/**
 * Determine the outcome from the perspective of the current player
 */
function determineOutcome(result: CombatResult, currentPlayerId: PlayerId): Outcome {
  if (result.winner === 'draw') {
    return 'draw';
  }
  return result.winner === currentPlayerId ? 'victory' : 'defeat';
}

/**
 * VictoryOverlay Component
 *
 * Full-screen overlay with semi-transparent backdrop that displays
 * the combat result with animations.
 */
export function VictoryOverlay({
  result,
  currentPlayerId,
  onReturnToLobby,
  onFightAgain,
  winnerDisplayName,
  seriesRecord,
}: VictoryOverlayProps) {
  // Capture battle log entries on mount (before they get cleared for next combat)
  const [battleLogEntries, setBattleLogEntries] = useState<CombatLogEntry[]>([]);

  useEffect(() => {
    // Get log entries from service when overlay mounts
    const logService = getCombatLogService();
    const entries = logService.getEntries();
    setBattleLogEntries(entries);
  }, []);

  // Determine outcome from current player's perspective
  const outcome = useMemo(
    () => determineOutcome(result, currentPlayerId),
    [result, currentPlayerId]
  );

  // Calculate creature performances from combat result
  const performances = useMemo(() => {
    if (!result.finalState?.units || !result.eventLog) {
      return [];
    }
    return calculateCreaturePerformance(
      result.finalState.units,
      result.eventLog,
      result.duration
    );
  }, [result.finalState?.units, result.eventLog, result.duration]);

  // Calculate MVP for each team
  const playerMVPUnitId = useMemo(
    () => calculateMVP(performances, currentPlayerId),
    [performances, currentPlayerId]
  );

  const opponentId: PlayerId = currentPlayerId === 'player1' ? 'player2' : 'player1';
  const opponentMVPUnitId = useMemo(
    () => calculateMVP(performances, opponentId),
    [performances, opponentId]
  );

  // Lock body scroll when overlay is mounted to prevent background scroll
  useEffect(() => {
    // Store original overflow value
    const originalOverflow = document.body.style.overflow;

    // Lock scroll
    document.body.style.overflow = 'hidden';

    // Cleanup: restore original overflow on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  return (
    <div className={styles.overlay}>
      {/* Semi-transparent backdrop */}
      <div className={styles.backdrop} />

      {/* Main content container */}
      <div className={styles.content}>
        {/* Outcome display (Victory/Defeat/Draw) - full width header */}
        <OutcomeDisplay
          outcome={outcome}
          reason={result.reason}
          winnerName={winnerDisplayName}
        />

        {/* Two-column layout: Results on left, Battle Log on right */}
        <div className={styles.twoColumnLayout}>
          {/* Left column: Statistics + Creature Performance */}
          <div className={styles.leftColumn}>
            {/* Statistics display */}
            {result.statistics && (
              <StatisticsDisplay
                statistics={result.statistics}
                currentPlayerId={currentPlayerId}
              />
            )}

            {/* Creature roster display */}
            {performances.length > 0 && (
              <CreatureRosterDisplay
                performances={performances}
                currentPlayerId={currentPlayerId}
                playerMVPUnitId={playerMVPUnitId}
                opponentMVPUnitId={opponentMVPUnitId}
              />
            )}
          </div>

          {/* Right column: Battle Log */}
          {battleLogEntries.length > 0 && (
            <div className={styles.rightColumn}>
              <BattleLogDisplay entries={battleLogEntries} defaultExpanded={true} />
            </div>
          )}
        </div>

        {/* Action buttons - full width footer */}
        <ActionButtons
          onFightAgain={onFightAgain}
          onLeaveMatch={onReturnToLobby}
          seriesRecord={seriesRecord}
          currentPlayerId={currentPlayerId}
        />
      </div>
    </div>
  );
}

export default VictoryOverlay;
